import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Reveal from "../components/site/Reveal";
import { CtaLink } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { FinalCta, PlatformsBand } from "../components/site/HomeSections";
import { CityFaq, CityWhy } from "../components/site/CitySeoBlocks";
import { CityServices, ServiceOtherCities, SVC_ICONS } from "../components/site/ServiceBlocks";
import { servicePage, serviceFaq, cityContent } from "../seo";
import { getCityPage, aCity } from "../mock";
import { api } from "../lib/api";

const Paras = ({ text }) => text.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>);

function Hero({ city, svc, ai }) {
  const at = aCity(city.name);
  const I = SVC_ICONS[svc.icon];
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#d9895a]/15 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <Reveal>
            <nav className="text-sm font-semibold text-[#6d7268]" data-testid="breadcrumb">
              <Link to="/" className="hover:text-[#13261c]">Accueil</Link> <span className="mx-1.5">/</span>
              <Link to="/villes" className="hover:text-[#13261c]">Villes</Link> <span className="mx-1.5">/</span>
              <Link to={`/villes/${city.slug}`} className="hover:text-[#13261c]" data-testid="svc-city-link">{city.name}</Link> <span className="mx-1.5">/</span>
              <Link to={`/services/${svc.slug}`} className="text-[#13261c] hover:underline" data-testid="svc-hub-link">{svc.name}</Link>
            </nav>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-[#13261c]" data-testid="svc-title">
              <span className="block font-logo font-black tracking-[-0.03em] text-[40px] sm:text-[56px] lg:text-[64px] leading-[1]">{svc.name}<OrganicDot className="inline-block align-baseline ml-1 w-3 h-3" /></span>
              <span className="block font-display font-bold text-[26px] sm:text-[34px] text-[#c4703f] leading-[1.2] mt-3">{at}</span>
            </h1>
          </Reveal>
          <Reveal delay={160}><p className="mt-5 text-lg text-[#4a5249] leading-relaxed max-w-xl" data-testid="svc-intro">{ai?.intro || svc.short(at)}</p></Reveal>
          <Reveal delay={220} className="mt-8 flex flex-wrap gap-3">
            <CtaLink to="/rendez-vous" testid="svc-rdv">Prendre rendez-vous</CtaLink>
            <CtaLink to={`/proprietaires?city=${encodeURIComponent(city.name)}`} variant="outline" testid="svc-sim">Simuler mon potentiel</CtaLink>
          </Reveal>
        </div>
        <Reveal delay={150} className="lg:col-span-5 relative">
          <div className="relative rounded-t-[200px] rounded-b-[36px] overflow-hidden aspect-[4/5] max-w-[420px] mx-auto shadow-[0_40px_80px_-30px_rgba(19,38,28,0.5)]">
            <img fetchPriority="high" src={city.img} alt={`${svc.name} ${at}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#13261c]/60 via-transparent to-transparent" />
            <p className="absolute bottom-6 left-6 text-white text-sm font-semibold flex items-center gap-1.5"><MapPin className="w-4 h-4" />{city.name} · {city.area}</p>
          </div>
          <span className="floaty absolute left-0 sm:left-4 top-10 w-16 h-16 rounded-3xl bg-white grid place-items-center shadow-[0_25px_50px_-20px_rgba(19,38,28,0.45)]"><I className="w-7 h-7 text-[#c4703f]" /></span>
        </Reveal>
      </div>
    </section>
  );
}

function Local({ city, svc }) {
  const c = cityContent(city.name);
  return (
    <section className="pb-16" data-testid="svc-local">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="rounded-[28px] bg-[#f3ede1] p-6 sm:p-8 grid md:grid-cols-3 gap-6">
          <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-[#c4703f]">{city.name}, {city.tagline}</p><p className="mt-2 text-[15px] text-[#4a5249] leading-relaxed">{city.desc || `${svc.name} ${aCity(city.name)} et dans les communes voisines, par notre équipe locale.`}</p></div>
          <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-[#c4703f]">Secteurs où nous intervenons</p><ul className="mt-2 flex flex-wrap gap-2">{city.spots.map((s) => <li key={s} className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-[#13261c]">{s}</li>)}</ul></div>
          <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-[#c4703f]">Repères</p><ul className="mt-2 space-y-1 text-sm text-[#13261c]">{c.near.map((h) => <li key={h.name} className="flex justify-between gap-3"><span>{h.name}</span><span className="font-bold whitespace-nowrap">{h.km} km</span></li>)}</ul></div>
        </div>
      </div>
    </section>
  );
}

function Included({ city, svc, ai }) {
  return (
    <section className="py-20" data-testid="svc-content">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
        <Reveal className="lg:col-span-4 lg:sticky lg:top-28 rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 grain relative overflow-hidden" data-testid="svc-included">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#d9895a]">Ce qui est inclus</p>
          <ul className="mt-6 space-y-4">
            {svc.points.map((t) => <li key={t} className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#d9895a] grid place-items-center shrink-0"><Check className="w-3.5 h-3.5 text-[#13261c]" strokeWidth={3} /></span>{t}</li>)}
          </ul>
          <p className="mt-6 text-sm text-[#faf7f0]/70 leading-relaxed" data-testid="svc-plans">{svc.plans}</p>
          <Link to="/tarifs" data-testid="svc-tarifs" className="mt-6 inline-flex rounded-full bg-[#d9895a] hover:bg-[#e39d72] text-[#13261c] px-6 py-3 font-bold transition-colors">Voir les formules</Link>
        </Reveal>
        <Reveal delay={100} className="lg:col-span-8 space-y-5 text-[17px] text-[#4a5249] leading-relaxed">
          {(ai?.sections || []).map((s, i) => (
            <div key={i} className="space-y-4 pt-2" data-testid={`svc-section-${i}`}>
              <h2 className="font-logo font-black tracking-[-0.02em] text-2xl lg:text-[28px] text-[#13261c] leading-tight">{s.title}</h2>
              <Paras text={s.text} />
            </div>
          ))}
          {!ai && <p>{svc.short(aCity(city.name))} {svc.plans}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function ServicePage() {
  const { slug, service } = useParams();
  const r = useMemo(() => servicePage(slug, service), [slug, service]);
  const key = `${slug}--${service}`;
  const { data: ai } = useQuery({ queryKey: ["city-content", key], queryFn: () => api.cityContent(key), enabled: !!r, retry: false, staleTime: 300_000 });
  if (!r) return <Navigate to={getCityPage(slug) ? `/villes/${slug}` : "/villes"} replace />;
  const city = getCityPage(slug);
  return (
    <div data-testid="service-page" key={key}>
      <Hero city={city} svc={r.svc} ai={ai} />
      <PlatformsBand />
      <Included city={city} svc={r.svc} ai={ai} />
      <Local city={city} svc={r.svc} />
      <CityWhy city={city} />
      <CityServices city={city} exclude={r.svc.slug} />
      <ServiceOtherCities city={city} svc={r.svc} />
      <CityFaq city={city} ai={ai} base={serviceFaq(r.svc, city, ai)} />
      <FinalCta />
    </div>
  );
}
